import { EMAIL_RE, normalizeLocalPhone } from './identity';

/**
 * Forgot-password core. Accepts an email or a phone; a phone is mapped to the
 * account's stored email. Pure with injected deps so the lookup, the
 * "no email on this account" case and the user-not-found swallow are tested
 * without Firebase.
 */
export type ResetOutcome = { ok: true } | { ok: false; reason: 'invalid' | 'no-email' | 'error' };
export type ResetDeps = {
  /** null when there is no such user OR the user has no email. */
  findEmailByPhone: (localPhone: string) => Promise<string | null>;
  sendReset: (email: string) => Promise<void>;
};

export async function requestPasswordResetCore(input: string, deps: ResetDeps): Promise<ResetOutcome> {
  const raw = String(input || '').trim();
  let email: string | null = null;

  if (EMAIL_RE.test(raw)) {
    email = raw.toLowerCase();
  } else {
    const local = normalizeLocalPhone(raw);
    if (!local) return { ok: false, reason: 'invalid' };
    try {
      email = await deps.findEmailByPhone(local);
    } catch (e: any) {
      return { ok: false, reason: 'error' };
    }
    if (!email) return { ok: false, reason: 'no-email' };
  }

  try {
    await deps.sendReset(email);
    return { ok: true };
  } catch (e: any) {
    // Identical outcome for an unknown address: the screen must not reveal
    // which emails have accounts.
    if (e?.code === 'auth/user-not-found') return { ok: true };
    return { ok: false, reason: 'error' };
  }
}
